/**
 * General info store – data from GET /api/general-info (dashboard bootstrap).
 * Loaded once after auth; hydrates authStore, configStore, modulesStore, languageStore and holds
 * account_units, tenant_users, positions, links, sidebar, pending_count, tasks, news, news_comment_counts, events, restaurants, restaurant_cities.
 */

import { create } from 'zustand';
import { authApi, type GeneralInfoPayload } from '@/lib/api';
import { useAuthStore } from '@/stores/authStore';
import { useConfigStore } from '@/stores/configStore';
import { useModulesStore, type License } from '@/stores/modulesStore';
import { useLanguageStore, type LanguageCode } from '@/stores/languageStore';
import type { TenantConfig } from '@/stores/configStore';

interface GeneralInfoState {
  /** Raw payload (for account_units, tenant_users, positions, etc.) */
  data: GeneralInfoPayload | null;
  loading: boolean;
  error: string | null;
  isLoaded: boolean;
  fetchGeneralInfo: () => Promise<void>;
  /** Getters for modal/page use */
  account_units: GeneralInfoPayload['account_units'];
  tenant_users: GeneralInfoPayload['tenant_users'];
  positions: GeneralInfoPayload['positions'];
  links: GeneralInfoPayload['links'];
  sidebar: GeneralInfoPayload['sidebar'];
  pending_count: number;
  tasks: GeneralInfoPayload['tasks'];
  news: GeneralInfoPayload['news'];
  news_comment_counts: Record<number, number>;
  events: GeneralInfoPayload['events'];
  vehicle_events: GeneralInfoPayload['vehicle_events'];
  restaurants: GeneralInfoPayload['restaurants'];
  restaurant_cities: GeneralInfoPayload['restaurant_cities'];
  clear: () => void;
}

export const useGeneralInfoStore = create<GeneralInfoState>((set, get) => ({
  data: null,
  loading: false,
  error: null,
  isLoaded: false,
  account_units: [],
  tenant_users: [],
  positions: [],
  links: [],
  sidebar: [],
  pending_count: 0,
  tasks: [],
  news: [],
  news_comment_counts: {},
  events: [],
  vehicle_events: [],
  restaurants: [],
  restaurant_cities: [],

  fetchGeneralInfo: async () => {
    set({ loading: true, error: null });
    try {
      const res = await authApi.getGeneralInfo();
      const payload = res.data?.data;
      if (!res.data?.success || !payload) {
        throw new Error('Invalid general-info response');
      }
      set({
        data: payload,
        account_units: payload.account_units ?? [],
        tenant_users: payload.tenant_users ?? [],
        positions: payload.positions ?? [],
        links: payload.links ?? [],
        sidebar: payload.sidebar ?? [],
        pending_count: payload.pending_count ?? 0,
        tasks: payload.tasks ?? [],
        news: payload.news ?? [],
        news_comment_counts: (payload.news_comment_counts as Record<number, number>) ?? {},
        events: payload.events ?? [],
        vehicle_events: payload.vehicle_events ?? [],
        restaurants: payload.restaurants ?? [],
        restaurant_cities: payload.restaurant_cities ?? [],
        loading: false,
        isLoaded: true,
        error: null,
      });

      // Hydrate auth store (user, tenant, tenants, settings, permissions)
      const tenants = payload.tenants as import('@/stores/authStore').Tenant[] | undefined;
      useAuthStore.getState().setFromGeneralInfo({
        user: payload.user as import('@/stores/authStore').User | null,
        tenant: payload.tenant ? (payload.tenant as import('@/stores/authStore').Tenant) : payload.tenant,
        tenants,
        needsTenantCreation: payload.needsTenantCreation ?? (tenants?.length === 0),
        settings: payload.settings,
        permissions: payload.permissions as import('@/stores/authStore').Permission[] | undefined,
      });

      // Hydrate language store from user's language preference
      const userLang = (payload.user as { language?: string } | null)?.language;
      if (userLang && ['sk', 'cs', 'en'].includes(userLang)) {
        useLanguageStore.getState().setLanguageFromBackend(userLang as LanguageCode);
      }

      // Hydrate config store (formatting)
      if (payload.formatting) {
        useConfigStore.getState().setConfig(payload.formatting as unknown as TenantConfig);
      }

      // Hydrate modules store (licenses + modules)
      const rawLicenses = payload.licenses ?? [];
      const licenses: License[] = rawLicenses.map((l: unknown) =>
        typeof l === 'string' ? { key: l } : (l as License)
      );
      const modules = (payload.modules ?? []) as import('@/lib/api').AppModule[];
      useModulesStore.getState().setFromBootstrap(licenses, modules);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load general info';
      set({ loading: false, error: message, isLoaded: false });
      throw err;
    }
  },

  clear: () =>
    set({
      data: null,
      account_units: [],
      tenant_users: [],
      positions: [],
      links: [],
      sidebar: [],
      pending_count: 0,
      tasks: [],
      news: [],
      news_comment_counts: {},
      events: [],
      vehicle_events: [],
      restaurants: [],
      restaurant_cities: [],
      isLoaded: false,
      error: null,
    }),
}));
